import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiCacheService } from '../cache-service/api-cache-service';

@Injectable({
  providedIn: 'root'
})
export class GeneralService {
  private http = inject(HttpClient);
  private baseURL: string = environment.HTTP;
  private cache = inject(ApiCacheService);

  private updateRequiredSubject = new BehaviorSubject<boolean>(false);
  updateRequired$ = this.updateRequiredSubject.asObservable();

  private apiIncotermResponsibility = `${this.baseURL}/General/IncotermResponsibility`;
  private apiPortType = `${this.baseURL}/General/PortType`;
  private apiRefCountry = `${this.baseURL}/General/RefCountry`;
  private apiReviewFreq = `${this.baseURL}/General/ReviewFrequency`;
  private apiSeverity = `${this.baseURL}/General/Severity`;
  private apiSupplierRegType = `${this.baseURL}/General/SupplierRegistrationType`;
  private apiLimitType = `${this.baseURL}/General/LimitType`;
  private apiLimitPeriod = `${this.baseURL}/General/LimitPeriod`;
  private apiRegType = `${this.baseURL}/General/CustomerRegistrationType`;
  private apiOwnershipType = `${this.baseURL}/General/GetOwnershipType`;
  private apiRefPrdCatItemType = `${this.baseURL}/General/RefPrdCatItemType`;
  private apiRefPlanningMethod = `${this.baseURL}/General/RefPlanningMethod`;
  private apiRefPostingClass = `${this.baseURL}/General/RefPostingClass`;
  private apiRefLotSerialClass = `${this.baseURL}/General/RefLotSerialClass`;
  private apiPrdInventoryType = `${this.baseURL}/General/PrdInventoryType`;
  private apiPrdCostingType = `${this.baseURL}/General/CostingType`;
  private apiPosContentType = `${this.baseURL}/General/PosContentType`;
  private apiGeneralModuleList = `${this.baseURL}/General/GeneralModuleList`;
  private apiMainModuleDropdown = `${this.baseURL}/General/GetMainModuleDropdown`;
  private apiSyncInterval = `${this.baseURL}/General/SyncInterval`;
  private apiOfflineDuration = `${this.baseURL}/General/OfflineDuration`;
  private apiAutoBackup = `${this.baseURL}/General/AutoBackup`;
  private apiInventoryCheckLevel = `${this.baseURL}/General/InventoryCheckLevel`;
  private apiRetryAttemptsDropdown = `${this.baseURL}/General/RetryAttemptsDropdown`;
  private apiRoundingMethodDropdown = `${this.baseURL}/General/GetRoundingMethodDropdown`;
  private apiGenerealAddressType = `${this.baseURL}/General/GeneralAddressType`;
  // private apiPosAssetTypeDropdown = `${this.baseURL}/General/RefPosAssetType`;
  private apiSupplierSiteType = `${this.baseURL}/General/SupplierSiteType`;
  private apiRefDiscountType = `${this.baseURL}/General/RefDiscountType`;
  private apiRefDiscountOption = `${this.baseURL}/General/RefDiscountOption`;
  private apiRefSalesDiscountType = `${this.baseURL}/General/RefSalesDiscountType`;
  private apiRefGroupDiscountType = `${this.baseURL}/General/RefGroupDiscountType`;
  private apiRefDiscountBy = `${this.baseURL}/General/RefDiscountBy`;
  private apiArApDefaultRate = `${this.baseURL}/General/ArApDefaultRate`;
  private apiGetUsageContextDropdown = `${this.baseURL}/General/GetUsageContextDropdown`;
  private apiGetRefRevaluationFrequencyDropDown = `${this.baseURL}/General/GetRefRevaluationFrequencyDropDown`;
  private apiGetRefUpdateFrequencyDropDown = `${this.baseURL}/General/GetRefUpdateFrequencyDropDown`;
  private apiGetRateTypeDropdown = `${this.baseURL}/General/GetRefRateTypeDropdown`;
  private apiGetRefCurrencyDropdown = `${this.baseURL}/General/GetRefCurrencyDropdown`;
  private apiDownloadFile = `${this.baseURL}/General/DownloadFile`;
  private apiGetLogActionDropdown = `${this.baseURL}/General/GetLogActionDropdown`;
  private apiUserStatusType = `${this.baseURL}/General/GetUserStatusType`;
  private apiStatusType = `${this.baseURL}/General/GetRefTaxStatus`;
  private apiTaxTxnType = `${this.baseURL}/General/GetRefTaxTxnType`;
  private apiTaxTxnGroup = `${this.baseURL}/General/GetRefTaxGroupDropdown`;
  private apiGetRefPosContentStatusDropdown = `${this.baseURL}/General/GetRefPosContentStatusDropdown`;
  private apiGetRefRequestStatusDropdown = `${this.baseURL}/General/GetRefRequestStatusDropdown`;
  private apiGetRefRequestStepStatusDropdown = `${this.baseURL}/General/GetRefRequestStepStatusDropdown`;
  private apiGetRefCurrencyNoBaseDropdown = `${this.baseURL}/General/GetRefCurrencyNoBaseDropdown`;
  private apiGetRefIrbmTypeDropdown = `${this.baseURL}/General/GetRefIrbmTypeDropdown`;
  private apiGetPricingTypeDropdown = `${this.baseURL}/General/RefPricingType`;
  private apiGetPricingMethodDropdown = `${this.baseURL}/General/RefPricingMethod`;
  private apiGetBusinessDayTypeDropdown = `${this.baseURL}/General/GetRefBusinessDayType`;
  private apiGetVoucherValidityTypeDropdown = `${this.baseURL}/General/GetRefVoucherValidityTypeDropdown`;
  private apiGetVoucherCategoryDropdown = `${this.baseURL}/General/GetRefVoucherCategoryType`;
  private apiGetVoucherChannelDropdown = `${this.baseURL}/General/GetRefVoucherChannelType`;
  private apiGetRefTaxExemptionCategoryDropdown = `${this.baseURL}/General/GetRefTaxExemptionCategoryDropdown`;
  private apiGetConditionLogicDropdown = `${this.baseURL}/General/GetConditionLogic`;
  private apiGetPurchaseConditionDropdown = `${this.baseURL}/General/RefPurchaseCondition`;
  private apiRefCostCalculationType = `${this.baseURL}/General/RefCostCalculationType`;
  private apiGstJurisdictionTypeDropdown = `${this.baseURL}/General/GetRefTaxJurisdictionTypeDropdown`;
  private apiUserRegistrationStatusDropdown = `${this.baseURL}/General/GetRefUserRegistrationStatus`;
  private apiGetDocumentTypeDropdown = `${this.baseURL}/General/DocumentType`;
  private apiPlCalcMethodDropdown = `${this.baseURL}/General/GetPlCalculationMethod`;
  private apiDocumentGroupingDropdown = `${this.baseURL}/General/DocumentGrouping`;
  private apiDocumentCategoryDropdown = `${this.baseURL}/General/DocumentCategory`;
  private apiTaxCalcMethodDropdown = `${this.baseURL}/General/GetRefTaxCalculationMethodDropdown`;
  private apiTaxDiscountBasesDropdown = `${this.baseURL}/General/GetRefTaxDiscountBasesDropdown`;
  private apiRefMixTypeDropdown = `${this.baseURL}/General/GetRefMixTypeDropdownAsync`;

  GetTaxCalcMethodDropdown() {
    return this.cache.get(`apiTaxCalcMethodDropdown`, () => this.http.get<any>(this.apiTaxCalcMethodDropdown));
  }

  GetTaxDiscountBasesDropdown() {
    return this.cache.get(`apiTaxDiscountBasesDropdown`, () => this.http.get<any>(this.apiTaxDiscountBasesDropdown));
  }

  GetVoucherCategory() {
    return this.cache.get(`apiGetVoucherCategoryDropdown`, () => this.http.get<any>(this.apiGetVoucherCategoryDropdown));
  }

  GetVoucherChannel() {
    return this.cache.get(`apiGetVoucherChannelDropdown`, () => this.http.get<any>(this.apiGetVoucherChannelDropdown));
  }

  GetVoucherValidityType() {
    return this.cache.get(`apiGetVoucherValidityTypeDropdown`, () => this.http.get<any>(this.apiGetVoucherValidityTypeDropdown));
  }

  GetBusinessDay() {
    return this.cache.get(`apiGetBusinessDayTypeDropdown`, () => this.http.get<any>(this.apiGetBusinessDayTypeDropdown));
  }

  GetIncotermResponsibility() {
    return this.cache.get(`apiIncotermResponsibility`, () => this.http.get<any>(this.apiIncotermResponsibility));
  }

  GetPortType() {
    return this.cache.get(`apiPortType`, () => this.http.get<any>(this.apiPortType));
  }

  GetRefCountry() {
    return this.cache.get(`apiRefCountry`, () => this.http.get<any>(this.apiRefCountry));
  }

  GetReviewFrequency() {
    return this.cache.get(`apiReviewFreq`, () => this.http.get<any>(this.apiReviewFreq));
  }

  GetSeverity() {
    return this.cache.get(`apiSeverity`, () => this.http.get<any>(this.apiSeverity));
  }

  GetSupplierRegType() {
    return this.cache.get(`apiSupplierRegType`, () => this.http.get<any>(this.apiSupplierRegType));
  }

  GetLimitType(searchModel?: any) {
    return this.cache.get(`apiLimitType`, () => this.http.get<any>(this.apiLimitType, searchModel));
  }

  GetLimitPeriod(searchModel?: any) {
    return this.cache.get(`apiLimitPeriod`, () => this.http.get<any>(this.apiLimitPeriod, searchModel));
  }

  GetRegType(searchModel?: any) {
    return this.cache.get(`apiRegType`, () => this.http.get<any>(this.apiRegType, searchModel));
  }

  GetOwnershipType() {
    return this.cache.get(`apiOwnershipType`, () => this.http.get<any>(this.apiOwnershipType));
  }

  setNavUpdateRequired(value: boolean) {
    this.updateRequiredSubject.next(value);
  }

  GetRefPrdCatItemType() {
    return this.cache.get(`apiRefPrdCatItemType`, () => this.http.get<any>(this.apiRefPrdCatItemType));
  }

  GetRefPlanningMethod() {
    return this.cache.get(`apiRefPlanningMethod`, () => this.http.get<any>(this.apiRefPlanningMethod));
  }

  GetRefPostingClass() {
    return this.cache.get(`apiRefPostingClass`, () => this.http.get<any>(this.apiRefPostingClass));
  }

  GetRefLotSerialClass() {
    return this.cache.get(`apiRefLotSerialClass`, () => this.http.get<any>(this.apiRefLotSerialClass));
  }

  GetInventoryType() {
    return this.cache.get(`apiPrdInventoryType`, () => this.http.get<any>(this.apiPrdInventoryType));
  }

  GetCostingType() {
    return this.cache.get(`apiPrdCostingType`, () => this.http.get<any>(this.apiPrdCostingType));
  }

  GetContentType() {
    return this.cache.get(`apiPosContentType`, () => this.http.get<any>(this.apiPosContentType));
  }

  GetGeneralModuleList() {
    return this.cache.get(`apiGeneralModuleList`, () => this.http.get<any>(this.apiGeneralModuleList));
  }

  getMainModuleDropdown() {
    return this.cache.get(`apiMainModuleDropdown`, () => this.http.get<any>(this.apiMainModuleDropdown));
  }

  GetSyncInterval() {
    return this.cache.get(`apiSyncInterval`, () => this.http.get<any>(this.apiSyncInterval));
  }

  GetOfflineDuration() {
    return this.cache.get(`apiOfflineDuration`, () => this.http.get<any>(this.apiOfflineDuration));
  }

  GetAutoBackup() {
    return this.cache.get(`apiAutoBackup`, () => this.http.get<any>(this.apiAutoBackup));
  }

  GetInventoryCheckLevel() {
    return this.cache.get(`apiInventoryCheckLevel`, () => this.http.get<any>(this.apiInventoryCheckLevel));
  }

  GetRetryAttemptsDropdown() {
    return this.cache.get(`apiRetryAttemptsDropdown`, () => this.http.get<any>(this.apiRetryAttemptsDropdown));
  }

  GetRoundingMethodDropdown() {
    return this.cache.get(`apiRoundingMethodDropdown`, () => this.http.get<any>(this.apiRoundingMethodDropdown));
  }

  GetGeneralAddressType() {
    return this.cache.get(`apiGenerealAddressType`, () => this.http.get<any>(this.apiGenerealAddressType));
  }

  // GetPosAssetTypeDropdown() {
  //   return this.cache.get(`apiPosAssetTypeDropdown`, () => this.http.get<any>(this.apiPosAssetTypeDropdown));
  // }

  GetSupplierSiteType() {
    return this.cache.get(`apiSupplierSiteType`, () => this.http.get<any>(this.apiSupplierSiteType));
  }

  GetRefDiscountType() {
    return this.cache.get(`apiRefDiscountType`, () => this.http.get<any>(this.apiRefDiscountType));
  }

  GetRefDiscountBy() {
    return this.cache.get(`apiRefDiscountBy`, () => this.http.get<any>(this.apiRefDiscountBy));
  }

  GetRefDiscountOption() {
    return this.cache.get(`apiRefDiscountOption`, () => this.http.get<any>(this.apiRefDiscountOption));
  }

  GetRefSalesDiscountType() {
    return this.cache.get(`apiRefSalesDiscountType`, () => this.http.get<any>(this.apiRefSalesDiscountType));
  }

  GetRefGroupDiscountType() {
    return this.cache.get(`apiRefGroupDiscountType`, () => this.http.get<any>(this.apiRefGroupDiscountType));
  }

  ArApDefaultRate() {
    return this.cache.get(`apiArApDefaultRate`, () => this.http.get<any>(this.apiArApDefaultRate));
  }

  GetUsageContextDropdown() {
    return this.cache.get(`apiGetUsageContextDropdown`, () => this.http.get<any>(this.apiGetUsageContextDropdown));
  }

  GetRefRevaluationFrequencyDropDown() {
    return this.cache.get(`apiGetRefRevaluationFrequencyDropDown`, () => this.http.get<any>(this.apiGetRefRevaluationFrequencyDropDown));
  }

  GetRefUpdateFrequencyDropDown() {
    return this.cache.get(`apiGetRefUpdateFrequencyDropDown`, () => this.http.get<any>(this.apiGetRefUpdateFrequencyDropDown));
  }

  GetRateTypeDropdown() {
    return this.cache.get(`apiGetRateTypeDropdown`, () => this.http.get<any>(this.apiGetRateTypeDropdown));
  }

  GetRefCurrencyDropdown() {
    return this.cache.get(`apiGetRefCurrencyDropdown`, () => this.http.get<any>(this.apiGetRefCurrencyDropdown));
  }

  DownloadFile(data: any) {
    return this.http.get(this.apiDownloadFile, { params: { filePath: data }, responseType: 'blob' });
  }

  getLogActionDropdown() {
    return this.cache.get(`apiGetLogActionDropdown`, () => this.http.get<any>(this.apiGetLogActionDropdown));
  }

  getUserStatusTypeDropdown() {
    return this.cache.get(`apiUserStatusType`, () => this.http.get<any>(this.apiUserStatusType));
  }

  GetStatusType() {
    return this.cache.get(`apiStatusType`, () => this.http.get<any>(this.apiStatusType));
  }

  GetTaxTxnType() {
    return this.cache.get(`apiTaxTxnType`, () => this.http.get<any>(this.apiTaxTxnType));
  }

  GetTaxTxnGroup() {
    return this.cache.get(`apiTaxTxnGroup`, () => this.http.get<any>(this.apiTaxTxnGroup));
  }

  GetRefPosContentStatusDropdown() {
    return this.cache.get(`apiGetRefPosContentStatusDropdown`, () => this.http.get<any>(this.apiGetRefPosContentStatusDropdown));
  }

  GetRefRequestStatusDropdown() {
    return this.cache.get(`apiGetRefRequestStatusDropdown`, () => this.http.get<any>(this.apiGetRefRequestStatusDropdown));
  }

  GetRefRequestStepStatusDropdown() {
    return this.cache.get(`apiGetRefRequestStepStatusDropdown`, () => this.http.get<any>(this.apiGetRefRequestStepStatusDropdown));
  }

  GetRefCurrencyNoBaseDropdown() {
    return this.cache.get(`apiGetRefCurrencyNoBaseDropdown`, () => this.http.get<any>(this.apiGetRefCurrencyNoBaseDropdown));
  }

  GetRefIrbmTypeDropdown() {
    return this.cache.get(`apiGetRefIrbmTypeDropdown`, () => this.http.get<any>(this.apiGetRefIrbmTypeDropdown));
  }

  getPricingTypeDropdown() {
    return this.cache.get(`apiGetPricingTypeDropdown`, () => this.http.get<any>(this.apiGetPricingTypeDropdown));
  }

  getPricingMethodDropdown() {
    return this.cache.get(`apiGetPricingMethodDropdown`, () => this.http.get<any>(this.apiGetPricingMethodDropdown));
  }

  getRefTaxExemptionCategoryDropdown() {
    return this.cache.get(`apiGetRefTaxExemptionCategoryDropdown`, () => this.http.get<any>(this.apiGetRefTaxExemptionCategoryDropdown));
  }

  getConditionLogicDropdown() {
    return this.cache.get(`apiGetConditionLogicDropdown`, () => this.http.get<any>(this.apiGetConditionLogicDropdown));
  }

  getPurchaseConditionDropdown() {
    return this.cache.get(`apiGetPurchaseConditionDropdown`, () => this.http.get<any>(this.apiGetPurchaseConditionDropdown));
  }

  getRefCostCalculationType() {
    return this.cache.get(`apiRefCostCalculationType`, () => this.http.get<any>(this.apiRefCostCalculationType));
  }

  getGstJurisdictionTypeDropdown() {
    return this.cache.get(`apiGstJurisdictionTypeDropdown`, () => this.http.get<any>(this.apiGstJurisdictionTypeDropdown));
  }

  getUserRegistrationStatusDropdown() {
    return this.cache.get(`apiUserRegistrationStatusDropdown`, () => this.http.get<any>(this.apiUserRegistrationStatusDropdown));
  }

  getDocumentTypeDropdown() {
    return this.cache.get(`apiGetDocumentTypeDropdown`, () => this.http.get<any>(this.apiGetDocumentTypeDropdown));
  }

  getPlCalcMethodDropdown() {
    return this.cache.get(`apiPlCalcMethodDropdown`, () => this.http.get<any>(this.apiPlCalcMethodDropdown));
  }

  getGroupingDropdown() {
    return this.cache.get(`apiDocumentGroupingDropdown`, () => this.http.get<any>(this.apiDocumentGroupingDropdown));
  }

  getCategoryDropdown() {
    return this.cache.get(`apiDocumentCategoryDropdown`, () => this.http.get<any>(this.apiDocumentCategoryDropdown));
  }

  getRefMixTypeDropdown() {
    return this.cache.get(`apiRefMixTypeDropdown`, () => this.http.get<any>(this.apiRefMixTypeDropdown));
  }
}
